import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthSessionContextValue {
  session: Session | null;
  user: User | null;
  isReady: boolean;
  refreshSession: () => Promise<Session | null>;
  acceptSession: (nextSession: Session) => void;
}

const AuthSessionContext = createContext<AuthSessionContextValue | undefined>(undefined);

export const AuthSessionProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isReady, setIsReady] = useState(false);
  const mountedRef = useRef(true);
  const sessionRef = useRef<Session | null>(null);

  const clearLogoutIntent = useCallback(() => {
    try { window.localStorage.removeItem('salesby:logout-intent-at'); } catch {}
    try { window.sessionStorage.removeItem('salesby:logout-intent-at'); } catch {}
  }, []);

  const hasRecentLogoutIntent = useCallback(() => {
    const cutoff = Date.now() - 5 * 60 * 1000;
    const values: Array<string | null> = [];
    try { values.push(window.localStorage.getItem('salesby:logout-intent-at')); } catch {}
    try { values.push(window.sessionStorage.getItem('salesby:logout-intent-at')); } catch {}
    return values.some((value) => {
      const timestamp = Number(value);
      return Number.isFinite(timestamp) && timestamp > cutoff;
    });
  }, []);

  const commitSession = useCallback((nextSession: Session | null) => {
    if (!mountedRef.current) return;
    if (nextSession) clearLogoutIntent();
    sessionRef.current = nextSession;
    setSession(nextSession);
    setIsReady(true);
  }, [clearLogoutIntent]);

  const refreshSession = useCallback(async () => {
    const { data: { session: nextSession }, error } = await supabase.auth.getSession();
    if (error) console.error('Error refreshing auth session:', error);

    if (!nextSession && sessionRef.current && !hasRecentLogoutIntent()) {
      setIsReady(true);
      return sessionRef.current;
    }

    commitSession(nextSession ?? null);
    return nextSession ?? null;
  }, [commitSession, hasRecentLogoutIntent]);

  const acceptSession = useCallback((nextSession: Session) => {
    commitSession(nextSession);
  }, [commitSession]);

  useEffect(() => {
    mountedRef.current = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if ((event === 'INITIAL_SESSION' || event === 'SIGNED_OUT') && !nextSession && sessionRef.current && !hasRecentLogoutIntent()) {
        setIsReady(true);
        return;
      }

      commitSession(nextSession ?? null);
    });

    void refreshSession();

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [commitSession, hasRecentLogoutIntent, refreshSession]);

  const value = useMemo<AuthSessionContextValue>(() => ({
    session,
    user: session?.user ?? null,
    isReady,
    refreshSession,
    acceptSession,
  }), [acceptSession, isReady, refreshSession, session]);

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
};

export const useAuthSession = () => {
  const context = useContext(AuthSessionContext);

  if (!context) {
    throw new Error("useAuthSession must be used within AuthSessionProvider");
  }

  return context;
};